<template>
  <div>
    <v-autocomplete
      id="municipio-select"
      v-model="filter.municipio"
      item-text="nome"
      item-value="municipioId"
      :items="municipios"
      :label="isSala ? 'Parceiros' : 'Municípios'"
      placeholder="Selecione..."
      :return-object="true"
      :disabled="disabled"
      @change="emitValue"
      :multiple="isMultiple"
      :solo="solo"
      :loading="loading"
      hide-details="auto"
      :outlined="outlined"
      :search-input.sync="searchText"
      @update:search-input="onSearchInput"
      ref="autocomplete"
    >
      <template slot="no-data">
        <div v-if="loading">
          <v-col align-self="center"
            ><div class="progress" v-if="loading">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular></div
          ></v-col>
        </div>
        <div v-else>Nenhum Município retornado</div>
      </template>

      <!-- Detectar scroll para carregar mais itens -->
      <template v-slot:append-item>
        <div
          v-if="podeCarregarMais"
          v-intersect="carregarMaisItens"
          class="px-4 py-2 text-center text-grey"
        >
          <v-progress-circular
            v-if="carregandoMais"
            indeterminate
            size="20"
            width="2"
          ></v-progress-circular>
          <span v-else>Carregar mais...</span>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { municipioAPI } from "@/modules/municipio";
import { mapGetters } from "vuex";

export default {
  name: "MunicipioCombobox",
  props: {
    filterMunicipios: { type: Array, default: () => [] },
    filter: { type: Object, required: true },
    disabled: { type: Boolean, required: false, default: false },
    solo: { type: Boolean, required: false, default: false },
    outlined: { type: Boolean, required: false, default: false },
    useConsolidado: { type: Boolean, required: false, default: false },

    isMultiple: { type: Boolean, required: false, default: false },
  },
  computed: {
    ...mapGetters(["isSala"]),
    podeCarregarMais() {
      return (
        !this.searchText &&
        this.municipiosOriginais &&
        this.municipios &&
        this.municipios.length < this.municipiosOriginais.length &&
        this.municipios.length >= this.municipiosCarregados
      );
    },
  },
  data() {
    return {
      todomunicipios: [],
      municipios: [],
      municipiosOriginais: [],
      municipiosCarregados: 200,
      incrementoCarregamento: 100,
      loading: true,
      carregandoMais: false,
      searchText: "",
    };
  },
  watch: {
    filter: {
      handler: function (newValue) {
        this.filtrarMunicipios(newValue);
      },
      deep: true,
    },
  },
  mounted() {
    // Inicialização segura dos arrays
    this.todomunicipios = [];
    this.municipiosOriginais = [];
    this.municipios = [];

    this.list();
  },
  methods: {
    emitValue(e) {
      // Validação para evitar erros quando o valor é limpo
      if (e === null || e === undefined) {
        this.$emit("changeMunicipio", null);
        return;
      }
      this.$emit("changeMunicipio", e);
    },
    onSearchInput(searchText) {
      // Validação para evitar erros
      if (!this.municipiosOriginais || this.municipiosOriginais.length === 0) {
        return;
      }

      if (searchText && searchText.trim().length >= 2) {
        const resultados = this.municipiosOriginais.filter(
          (item) =>
            item &&
            item.nome &&
            typeof item.nome === "string" &&
            item.nome.toLowerCase().includes(searchText.toLowerCase())
        );
        this.municipios = resultados;
      } else {
        this.atualizarListaMunicipios();
      }
    },
    carregarMaisItens() {
      if (this.carregandoMais || this.searchText) return;

      this.carregandoMais = true;

      setTimeout(() => {
        this.municipiosCarregados += this.incrementoCarregamento;
        this.atualizarListaMunicipios();
        this.carregandoMais = false;
      }, 300);
    },
    atualizarListaMunicipios() {
      // Validação para evitar erros
      if (!this.municipiosOriginais || this.municipiosOriginais.length === 0) {
        this.municipios = [];
        return;
      }

      const limite = Math.min(
        this.municipiosCarregados,
        this.municipiosOriginais.length
      );
      this.municipios = this.municipiosOriginais.slice(0, limite);
    },
    filtrarMunicipios(newValue) {
      // Validação inicial
      if (
        !newValue ||
        !this.todomunicipios ||
        this.todomunicipios.length === 0
      ) {
        return;
      }

      let municipiosFiltrados = this.todomunicipios.filter(
        (item) => item && item.nome && typeof item.nome === "string"
      );

      if (newValue.uf && newValue.uf.length) {
        municipiosFiltrados = municipiosFiltrados.filter((item) =>
          newValue.uf.some((uf) => uf.uf === item.uf || uf === item.uf)
        );
      }

      if (newValue.relacionamento && newValue.relacionamento.length) {
        const elegivelSelecionado = newValue.relacionamento.some(
          (r) => r.statusId === 10
        );
        municipiosFiltrados = municipiosFiltrados.filter(
          (item) =>
            newValue.relacionamento.some(
              (r) => r.statusId === this.normalizarStatusId(item.statusId)
            ) ||
            (elegivelSelecionado && this.isElegivelIan(item.elegivelIan))
        );
      }

      if (newValue.contrato && newValue.contrato.length) {
        municipiosFiltrados = municipiosFiltrados.filter((item) =>
          newValue.contrato.some(
            (r) => r.statusContratoId === item.statusContratoId
          )
        );
      }

      // Ordenar
      this.municipiosOriginais = municipiosFiltrados.sort((a, b) => {
        if (a.nome < b.nome) return -1;
        if (a.nome > b.nome) return 1;
        return 0;
      });

      // Inicializar com carregamento incremental
      this.municipiosCarregados = 200;
      this.atualizarListaMunicipios();
    },
    list() {
      const request = this.useConsolidado
        ? municipioAPI.listCidadeEmpreendedora()
        : municipioAPI.list(this.$store.state.uf);

      request
        .then((resp) => {
          this.todomunicipios = resp.data || [];

          const dadosLimpos = (resp.data || []).filter(
            (item) =>
              item &&
              item.nome &&
              typeof item.nome === "string" &&
              item.nome.trim() !== ""
          );

          this.municipiosOriginais = dadosLimpos;
          this.municipios = dadosLimpos.slice(0, 200); // Começar com 200

          // Aplicar filtros se existirem
          if (this.filter) {
            this.filtrarMunicipios(this.filter);
          }

          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.municipios = [];
          this.municipiosOriginais = [];
          this.todomunicipios = [];
          this.$notification.error("Erro ao listar municípios");
        });
    },
    isElegivelIan(valor) {
      if (valor === null || valor === undefined) {
        return false;
      }

      if (typeof valor === "boolean") {
        return valor;
      }

      const texto = String(valor).trim().toLowerCase();
      if (texto === "sim" || texto === "true") {
        return true;
      }

      if (texto === "nao" || texto === "não" || texto === "false") {
        return false;
      }

      const parsed = Number(texto);
      return !Number.isNaN(parsed) && parsed !== 0;
    },
    normalizarStatusId(valor) {
      if (valor === null || valor === undefined || valor === "") {
        return null;
      }

      const parsed = Number(valor);
      return Number.isNaN(parsed) ? null : parsed;
    },
  },
};
</script>
